import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  Lightbulb,
  HelpCircle,
  FileText,
  Plus,
  X,
  Sparkles,
  Check,
} from "lucide-react";
import Card from "../components/Card";
import Button from "../components/Button";
import { Input, Textarea } from "../components/Input";
import LoadingSkeleton from "../components/LoadingSkeleton";
import { apiRequest } from "../lib/queryClient";
import type { Call, Participant, CriticalInsight, StrategicQuestion } from "../types";

const STEPS = [
  { id: 1, title: "Participants", icon: Users },
  { id: 2, title: "Insights", icon: Lightbulb },
  { id: 3, title: "Questions", icon: HelpCircle },
  { id: 4, title: "Summary", icon: FileText },
];

export default function PreparationPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [call, setCall] = useState<Call | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);

  const [newParticipant, setNewParticipant] = useState({
    name: "",
    title: "",
    company: "",
  });
  const [researchingId, setResearchingId] = useState<string | null>(null);
  const [newInsight, setNewInsight] = useState("");
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);

  useEffect(() => {
    if (id) {
      loadCall(id);
    }
  }, [id]);

  const loadCall = async (callId: string) => {
    try {
      const response = await fetch(`/api/calls/${callId}`);
      if (response.ok) {
        const data = await response.json();
        setCall(data);
        if (data?.prepPhase?.currentStep) {
          setCurrentStep(data.prepPhase.currentStep);
        }
      }
    } catch (error) {
      console.error("Error loading call:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateCall = async (updates: Partial<Call>) => {
    if (!call) return null;
    try {
      const response = await apiRequest("PUT", `/api/calls/${call.id}`, updates);
      if (response.ok) {
        const updated = await response.json();
        setCall(updated);
        return updated;
      }
    } catch (error) {
      console.error("Error updating call:", error);
    }
    return null;
  };

  const updatePrepPhase = async (updates: Partial<Call["prepPhase"]>) => {
    if (!call) return;
    const prepPhase = {
      status: "in_progress" as const,
      currentStep,
      criticalInsights: [],
      strategicQuestions: [],
      ...call.prepPhase,
      ...updates,
    };
    await updateCall({ prepPhase });
  };

  const handleAddParticipant = async () => {
    if (!call || !newParticipant.name) return;
    try {
      const response = await apiRequest("POST", `/api/calls/${call.id}/participants`, newParticipant);
      if (response.ok) {
        await loadCall(String(call.id));
        setNewParticipant({ name: "", title: "", company: "" });
      }
    } catch (error) {
      console.error("Error adding participant:", error);
    }
  };

  const handleResearchParticipant = async (participant: Participant) => {
    if (!call) return;
    setResearchingId(String(participant.id));
    try {
      const response = await apiRequest("POST", "/api/ai/research-participant", {
        name: participant.name,
        company: participant.company || call.companyName,
        title: participant.title,
      });
      const data = await response.json();
      await apiRequest("PUT", `/api/calls/${call.id}/participants/${participant.id}`, {
        profileSummary: data.summary,
      });
      await loadCall(String(call.id));
    } catch (error) {
      console.error("Error researching participant:", error);
    } finally {
      setResearchingId(null);
    }
  };

  const handleRemoveParticipant = async (participantId: number) => {
    if (!call) return;
    try {
      await apiRequest("DELETE", `/api/calls/${call.id}/participants/${participantId}`);
      await loadCall(String(call.id));
    } catch (error) {
      console.error("Error removing participant:", error);
    }
  };

  const handleAddInsight = async () => {
    if (!newInsight.trim()) return;
    const insights: CriticalInsight[] = [
      ...(call?.prepPhase?.criticalInsights || []),
      { id: Date.now().toString(), insight: newInsight },
    ];
    await updatePrepPhase({ criticalInsights: insights });
    setNewInsight("");
  };

  const handleRemoveInsight = async (insightId: string) => {
    const insights = (call?.prepPhase?.criticalInsights || []).filter(
      (i) => i.id !== insightId
    );
    await updatePrepPhase({ criticalInsights: insights });
  };

  const handleGenerateQuestions = async () => {
    if (!call) return;
    setIsGeneratingQuestions(true);
    try {
      const participants = call.participants || [];
      const response = await apiRequest("POST", "/api/ai/generate-questions", {
        clientName: call.clientName,
        goals: call.goals,
        participants: participants.map((p) => ({
          name: p.name,
          title: p.title,
          profileSummary: p.profileSummary,
        })),
        criticalInsights: call.prepPhase?.criticalInsights,
        industry: call.companyName,
      });
      const data = await response.json();
      const questions: StrategicQuestion[] = data.questions.map(
        (q: { question: string; purpose: string; category: string }, i: number) => ({
          id: `q-${i}`,
          question: q.question,
          purpose: q.purpose,
          category: q.category,
        })
      );
      await updatePrepPhase({ strategicQuestions: questions });
    } catch (error) {
      console.error("Error generating questions:", error);
    } finally {
      setIsGeneratingQuestions(false);
    }
  };

  const handleGenerateSummary = async () => {
    if (!call) return;
    setIsGeneratingSummary(true);
    try {
      const participants = call.participants || [];
      const response = await apiRequest("POST", "/api/ai/generate-prep-summary", {
        clientName: call.clientName,
        goals: call.goals,
        participants: participants.map((p) => ({
          name: p.name,
          title: p.title,
        })),
        criticalInsights: call.prepPhase?.criticalInsights,
        strategicQuestions: call.prepPhase?.strategicQuestions,
      });
      const data = await response.json();
      await updatePrepPhase({
        prepSummary: data.summary,
        status: "completed",
        completedAt: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Error generating summary:", error);
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const goToStep = async (step: number) => {
    setCurrentStep(step);
    await updatePrepPhase({ currentStep: step });
  };

  if (isLoading) {
    return <LoadingSkeleton count={3} />;
  }

  if (!call) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Call not found</p>
        <Button variant="ghost" onClick={() => navigate("/calls")} className="mt-4">
          Back to Calls
        </Button>
      </div>
    );
  }

  const participants = call.participants || [];

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <Card>
              <h3 className="font-semibold text-gray-900 mb-4">Add Participant</h3>
              <div className="mb-3">
                <Input
                  placeholder="Name"
                  value={newParticipant.name}
                  onChange={(e) =>
                    setNewParticipant({ ...newParticipant, name: e.target.value })
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <Input
                  placeholder="Title"
                  value={newParticipant.title}
                  onChange={(e) =>
                    setNewParticipant({ ...newParticipant, title: e.target.value })
                  }
                />
                <Input
                  placeholder="Company"
                  value={newParticipant.company}
                  onChange={(e) =>
                    setNewParticipant({ ...newParticipant, company: e.target.value })
                  }
                />
              </div>
              <Button
                onClick={handleAddParticipant}
                disabled={!newParticipant.name}
                icon={<Plus className="w-4 h-4" />}
              >
                Add
              </Button>
            </Card>

            {participants.map((p) => (
              <Card key={p.id}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-medium text-gray-900">{p.name}</h4>
                    {p.title && <p className="text-sm text-gray-500">{p.title}</p>}
                    {p.company && <p className="text-sm text-gray-500">{p.company}</p>}
                  </div>
                  <button
                    onClick={() => handleRemoveParticipant(p.id)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {p.profileSummary ? (
                  <p className="text-sm text-gray-600 bg-primary/5 p-3 rounded-lg">
                    {p.profileSummary}
                  </p>
                ) : (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleResearchParticipant(p)}
                    loading={researchingId === String(p.id)}
                    icon={<Sparkles className="w-3 h-3" />}
                  >
                    Research
                  </Button>
                )}
              </Card>
            ))}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <Card>
              <h3 className="font-semibold text-gray-900 mb-3">Critical Insights</h3>
              <p className="text-sm text-gray-500 mb-4">
                Capture what you know about this opportunity
              </p>
              <div className="flex gap-2 mb-4">
                <Textarea
                  placeholder="e.g., Budget is $50k, decision by Q2"
                  value={newInsight}
                  onChange={(e) => setNewInsight(e.target.value)}
                  rows={2}
                  className="flex-1"
                />
              </div>
              <Button onClick={handleAddInsight} disabled={!newInsight.trim()}>
                Add Insight
              </Button>
            </Card>

            {(call.prepPhase?.criticalInsights || []).map((insight) => (
              <Card key={insight.id}>
                <div className="flex justify-between items-start">
                  <p className="text-gray-700">{insight.insight}</p>
                  <button
                    onClick={() => handleRemoveInsight(insight.id)}
                    className="text-gray-400 hover:text-red-500 ml-2"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </Card>
            ))}
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <Card>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900">Strategic Questions</h3>
                  <p className="text-sm text-gray-500">
                    AI-generated questions based on your preparation
                  </p>
                </div>
                <Button
                  onClick={handleGenerateQuestions}
                  loading={isGeneratingQuestions}
                  icon={<Sparkles className="w-4 h-4" />}
                >
                  {(call.prepPhase?.strategicQuestions || []).length > 0
                    ? "Regenerate"
                    : "Generate"}
                </Button>
              </div>
            </Card>

            {(call.prepPhase?.strategicQuestions || []).map((q) => (
              <Card key={q.id}>
                <span className="inline-block text-xs font-medium uppercase text-primary bg-primary/10 px-2 py-1 rounded mb-2">
                  {q.category}
                </span>
                <p className="font-medium text-gray-900 mb-1">{q.question}</p>
                <p className="text-sm text-gray-500">{q.purpose}</p>
              </Card>
            ))}
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <Card>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900">Preparation Summary</h3>
                  <p className="text-sm text-gray-500">
                    Your executive brief for this call
                  </p>
                </div>
                <Button
                  onClick={handleGenerateSummary}
                  loading={isGeneratingSummary}
                  icon={<Sparkles className="w-4 h-4" />}
                >
                  {call.prepPhase?.prepSummary ? "Regenerate" : "Generate"}
                </Button>
              </div>

              {call.prepPhase?.prepSummary ? (
                <div className="prose prose-sm max-w-none">
                  <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 bg-gray-50 p-4 rounded-lg">
                    {call.prepPhase.prepSummary}
                  </pre>
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  Generate a summary once you've completed the other steps
                </p>
              )}
            </Card>

            {call.prepPhase?.status === "completed" && (
              <div className="flex items-center gap-2 text-green-600 bg-green-50 p-4 rounded-xl">
                <Check className="w-5 h-5" />
                <span className="font-medium">Preparation Complete!</span>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <button
        onClick={() => navigate(`/calls/${call.id}`)}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Call
      </button>

      <h1 className="text-2xl font-bold text-gray-900 mb-2">Prepare for Call</h1>
      <p className="text-gray-500 mb-6">{call.clientName}</p>

      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {STEPS.map((step) => (
          <button
            key={step.id}
            onClick={() => goToStep(step.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap transition-colors ${
              currentStep === step.id
                ? "bg-primary text-white"
                : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
            }`}
          >
            <step.icon className="w-4 h-4" />
            {step.title}
          </button>
        ))}
      </div>

      {renderStep()}

      <div className="flex justify-between mt-8">
        <Button
          variant="outline"
          onClick={() => goToStep(currentStep - 1)}
          disabled={currentStep === 1}
        >
          Previous
        </Button>
        {currentStep < 4 ? (
          <Button onClick={() => goToStep(currentStep + 1)}>Next</Button>
        ) : (
          <Button onClick={() => navigate(`/calls/${call.id}`)}>Done</Button>
        )}
      </div>
    </div>
  );
}
